/**
 * Modal for reassigning a box's marker to a different type/ID.
 */

import { useState, useEffect } from 'react';
import { EditModal } from './EditModal';
import { useBoxStore } from '../store/BoxContext';
import { useToast } from '../store/ToastContext';
import { getArucoSvgUrl, getQrSvgUrl, checkMarkerAvailable } from '../api/markers';
import type { Box, MarkerType } from '@shared/types';

interface ReassignMarkerModalProps {
  box: Box;
  onClose: () => void;
}

export function ReassignMarkerModal({ box, onClose }: ReassignMarkerModalProps) {
  const { updateBox } = useBoxStore();
  const { addToast } = useToast();
  const [markerType, setMarkerType] = useState<MarkerType>(box.markerType);
  const [markerId, setMarkerId] = useState(box.markerId);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  // Validate marker availability when user changes marker
  useEffect(() => {
    if (!markerId.trim()) {
      setIsAvailable(null);
      return;
    }

    // Skip validation if marker hasn't changed
    if (markerType === box.markerType && markerId === box.markerId) {
      setIsAvailable(true);
      return;
    }

    const timer = setTimeout(async () => {
      setValidating(true);
      try {
        const available = await checkMarkerAvailable(markerType, markerId);
        setIsAvailable(available);
      } catch (err) {
        console.error('Failed to check marker availability:', err);
        setIsAvailable(null);
      } finally {
        setValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [markerType, markerId, box.markerType, box.markerId]);

  function getPreviewUrl(type: MarkerType, id: string, size = 128): string {
    if (type === 'aruco') {
      const numId = parseInt(id, 10);
      if (isNaN(numId) || numId < 0 || numId > 49) return '';
      return getArucoSvgUrl(numId, size);
    }
    return id.trim() ? getQrSvgUrl(id, size) : '';
  }

  async function handleSave() {
    if (!markerId.trim()) {
      addToast('error', 'Marker ID is required');
      return;
    }

    if (markerType === 'aruco') {
      const numId = parseInt(markerId, 10);
      if (isNaN(numId) || numId < 0 || numId > 49) {
        addToast('error', 'ArUco ID must be between 0 and 49');
        return;
      }
    }

    if (isAvailable === false) {
      addToast('error', 'This marker is already in use');
      return;
    }

    setSaving(true);
    try {
      await updateBox(box.id, {
        markerType,
        markerId: markerId.trim(),
      });
      addToast('success', 'Marker reassigned successfully');
      onClose();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to reassign marker';
      addToast('error', errorMsg);
    } finally {
      setSaving(false);
    }
  }

  const hasChanges = markerType !== box.markerType || markerId !== box.markerId;
  const canSave = hasChanges && !validating && isAvailable === true && markerId.trim().length > 0;

  return (
    <EditModal open={true} onClose={onClose} title="Reassign Marker">
      <div className="space-y-4">
        {/* Current Marker */}
        <div className="rounded-lg border border-border bg-surface-hover p-4">
          <h3 className="mb-2 text-sm font-medium text-text-secondary">Current Marker</h3>
          <div className="flex items-center gap-3">
            <img
              src={getPreviewUrl(box.markerType, box.markerId, 64)}
              alt={`${box.markerType} ${box.markerId}`}
              className="h-16 w-16 rounded border border-border"
            />
            <div>
              <p className="text-sm font-medium text-text-primary">
                {box.markerType.toUpperCase()}: {box.markerId}
              </p>
              <p className="text-xs text-text-secondary">Box: {box.name}</p>
            </div>
          </div>
        </div>

        {/* New Marker Type */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">Marker Type</label>
          <select
            value={markerType}
            onChange={(e) => {
              const newType = e.target.value as MarkerType;
              setMarkerType(newType);
              if (
                newType === 'aruco' &&
                (parseInt(markerId, 10) < 0 || parseInt(markerId, 10) > 49)
              ) {
                setMarkerId('0');
              }
            }}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
          >
            <option value="aruco">ArUco (IDs 0-49)</option>
            <option value="qr">QR Code (custom ID)</option>
          </select>
        </div>

        {/* New Marker ID */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">Marker ID</label>
          {markerType === 'aruco' ? (
            <input
              type="number"
              min="0"
              max="49"
              value={markerId}
              onChange={(e) => setMarkerId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
              placeholder="0-49"
            />
          ) : (
            <input
              type="text"
              value={markerId}
              onChange={(e) => setMarkerId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
              placeholder="e.g., winter-clothes"
            />
          )}
          {validating && (
            <p className="mt-1 text-xs text-text-secondary">Checking availability...</p>
          )}
          {!validating && isAvailable === false && hasChanges && (
            <p className="mt-1 text-xs text-red-500">This marker is already in use</p>
          )}
          {!validating && isAvailable === true && hasChanges && (
            <p className="mt-1 text-xs text-green-600 dark:text-green-400">Marker is available</p>
          )}
        </div>

        {/* New Marker Preview */}
        {markerId.trim() && getPreviewUrl(markerType, markerId) && (
          <div className="rounded-lg border border-border bg-surface-hover p-4">
            <h3 className="mb-2 text-sm font-medium text-text-secondary">New Marker Preview</h3>
            <div className="flex items-center justify-center">
              <img
                src={getPreviewUrl(markerType, markerId)}
                alt={`${markerType} ${markerId}`}
                className="h-32 w-32 rounded border border-border"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-hover disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="flex-1 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Reassign Marker'}
          </button>
        </div>
      </div>
    </EditModal>
  );
}
